import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, Smartphone, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Zeigt einen QR-Code (Payrexx-Zahlseite) am iPad. Kunde scannt mit dem Handy
// und zahlt via TWINT/Karte. Der Payrexx-Webhook markiert die Zahlung als
// bezahlt; dieser Dialog pollt den Status und zeigt dann ✅.
//
// Zwei Modi:
//  1) invoice-basiert (bestehende Rechnung bezahlen, z. B. Rechnungsliste):
//     prop `invoice` → erzeugt Zahllink über /invoices/{id}/request-payment
//     und pollt /invoices/{id}/payment-status.
//  2) deferred (Termin-Quick-Checkout TWINT): prop `deferred`
//     = { purchaseRef, paymentLink, amount, invoiceNumber? }. Es wird NICHTS
//     vorab gebucht (keine Rechnung, kein Guthabenabzug, kein Check-in) — der
//     Betrag ist der Restbetrag NACH Guthaben/Punkten. Poll über
//     /appointments/pending-checkout/{ref}/status; beim Schliessen ohne
//     bestätigte Zahlung wird /appointments/pending-checkout/{ref}/cancel
//     aufgerufen (Rollback → Termin bleibt offen).
export const QrPayDialog = ({ invoice, deferred, open, onOpenChange, onPaid }) => {
  const [link, setLink] = useState('');
  const [status, setStatus] = useState('idle'); // idle|creating|waiting|paid|error
  const pollRef = useRef(null);
  const paidRef = useRef(false);

  const isDeferred = !!deferred?.purchaseRef;
  const purchaseRef = deferred?.purchaseRef;
  const invoiceId = invoice?.id;
  const invoiceNumber = isDeferred ? deferred?.invoiceNumber : invoice?.invoice_number;
  const amount = isDeferred
    ? Number(deferred?.amount ?? 0)
    : (invoice ? Number(invoice.total ?? invoice.total_amount ?? invoice.amount ?? 0) : 0);
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  // Invoice-Modus: Zahllink beim Öffnen erzeugen.
  const createLink = useCallback(async () => {
    if (!invoiceId) return;
    setStatus('creating');
    setLink('');
    try {
      const r = await axios.post(`${API}/invoices/${invoiceId}/request-payment?channel=none`, {}, authHeaders());
      const l = r.data?.payment_link;
      if (!l) throw new Error('Kein Zahlungslink erhalten');
      setLink(l);
      setStatus('waiting');
    } catch (e) {
      setStatus('error');
      toast.error(e.response?.data?.detail || 'Zahlungslink konnte nicht erstellt werden');
    }
  }, [invoiceId]);

  // Status-Polling während des Wartens (beide Modi)
  useEffect(() => {
    if (!open || status !== 'waiting') return undefined;
    if (isDeferred && !purchaseRef) return undefined;
    if (!isDeferred && !invoiceId) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const url = isDeferred
          ? `${API}/appointments/pending-checkout/${purchaseRef}/status`
          : `${API}/invoices/${invoiceId}/payment-status`;
        const r = await axios.get(url, authHeaders());
        if (r.data?.paid) {
          clearInterval(pollRef.current);
          paidRef.current = true;
          setStatus('paid');
          toast.success('Zahlung erhalten ✅');
          if (onPaid) onPaid();
        }
      } catch (_) {
        /* transient – weiter pollen */
      }
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, [open, status, isDeferred, purchaseRef, invoiceId, onPaid]);

  // Beim Öffnen: Link setzen/erzeugen; beim Schliessen: aufräumen
  useEffect(() => {
    if (open) {
      paidRef.current = false;
      if (isDeferred) {
        // Zahllink kommt bereits vom Defer-Endpoint — direkt anzeigen.
        setLink(deferred?.paymentLink || '');
        setStatus(deferred?.paymentLink ? 'waiting' : 'error');
      } else if (invoiceId) {
        createLink();
      }
    } else {
      setStatus('idle');
      setLink('');
      if (pollRef.current) clearInterval(pollRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoiceId, isDeferred, purchaseRef, createLink]);

  // Schliessen: im Defer-Modus geparkten Checkout abbrechen (Rollback), falls
  // noch nicht bezahlt — sonst bliebe der Vorgang offen liegen.
  const handleOpenChange = async (o) => {
    if (!o && isDeferred && purchaseRef && !paidRef.current) {
      try {
        await axios.post(`${API}/appointments/pending-checkout/${purchaseRef}/cancel`, {}, authHeaders());
      } catch (_) {
        /* Best effort — Backend räumt geparkte Checkouts ohnehin auf. */
      }
    }
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="qr-pay-dialog">
        <DialogHeader>
          <DialogTitle>Mit TWINT / Karte bezahlen</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-2 text-center">
          <div className="text-3xl font-bold" data-testid="qr-pay-amount">CHF {amount.toFixed(2)}</div>
          {invoiceNumber && (
            <div className="text-sm text-muted-foreground">Rechnung {invoiceNumber}</div>
          )}

          {status === 'creating' && <Loader2 className="w-12 h-12 animate-spin text-muted-foreground my-6" />}

          {status === 'waiting' && link && (
            <>
              <div className="rounded-xl bg-white p-4 border shadow-sm" data-testid="qr-pay-code">
                <QRCodeSVG value={link} size={230} level="M" />
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Smartphone className="w-4 h-4" />
                <span>Mit dem Handy scannen → TWINT oder Karte wählen</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-yellow-600 font-medium">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Warte auf Zahlung…
              </div>
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-600 underline break-all max-w-full"
                data-testid="qr-pay-link"
              >
                Zahlseite direkt öffnen
              </a>
              <Button
                variant="outline"
                size="sm"
                className="mt-1"
                onClick={() => handleOpenChange(false)}
                data-testid="qr-pay-cancel"
              >
                Zahlung abbrechen
              </Button>
            </>
          )}

          {status === 'paid' && (
            <div className="flex flex-col items-center gap-2 py-6" data-testid="qr-pay-success">
              <CheckCircle2 className="w-20 h-20 text-green-600" />
              <div className="text-xl font-semibold text-green-700">Bezahlt!</div>
              <Button className="mt-2" onClick={() => onOpenChange(false)} data-testid="qr-pay-close">
                Schliessen
              </Button>
            </div>
          )}

          {status === 'error' && !isDeferred && (
            <Button variant="outline" onClick={createLink} data-testid="qr-pay-retry" className="my-4">
              <RefreshCw className="w-4 h-4 mr-2" /> Erneut versuchen
            </Button>
          )}
          {status === 'error' && isDeferred && (
            <div className="text-sm text-red-600 my-4">Kein Zahlungslink verfügbar. Bitte erneut versuchen.</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QrPayDialog;
